import React, { useEffect, useState } from 'react'
import { createContext } from 'lib/createContext'
import { parseErrorMessage } from 'api'
import type { JetAssignmentItem } from 'api/admin/jet/listJetAssignments'
import { useJetAssignmentsQuery } from 'queries/jet/useJetAssignmentsQuery'
import { useJetAssignmentsFiltersQueryState } from '../hooks/useJetAssignmentsFiltersQueryState'

type JetAssignmentsFilters = ReturnType<
  typeof useJetAssignmentsFiltersQueryState
>[0]

const _PAGE_SIZE = 50

type JetAssignmentsContextState = {
  state: {
    filters: JetAssignmentsFilters
    items: JetAssignmentItem[]
    // Total matching the current filters, across all pages.
    totalCount: number
    // 1-based index of the page currently shown.
    pageIndex: number
    hasNextPage: boolean
    hasPrevPage: boolean
    isPending: boolean
    isFetching: boolean
    isError: boolean
    error: string
    dataUpdatedAt: number
  }
  actions: {
    setFilters: (update: Partial<JetAssignmentsFilters>) => void
    goNextPage: () => void
    goPrevPage: () => void
    refetch: () => void
  }
}

const [Context, useJetAssignmentsContextValue] =
  createContext<JetAssignmentsContextState>({
    name: 'JetAssignmentsContext',
  })

// A keyset cursor stack: each entry is the cursor used to fetch that page
// (`undefined` == page 1). Next pushes the last response's cursor; Prev pops.
type CursorStack = Array<string | undefined>

export const JetAssignmentsProvider = ({
  children,
}: {
  children: React.ReactNode
}) => {
  const [filters, setFilters] = useJetAssignmentsFiltersQueryState()

  // Everything that defines the list except the cursor. When it changes we must
  // restart paging from page 1, because a cursor is only valid for the sort it
  // was minted under.
  const signature = JSON.stringify({
    statuses: filters.statuses,
    assignmentId: filters.assignmentId,
    shiftId: filters.shiftId,
    dateField: filters.dateField,
    daysAgo: filters.daysAgo,
    sortBy: filters.sortBy,
    sortDir: filters.sortDir,
  })

  const [pageState, setPageState] = useState<{
    signature: string
    stack: CursorStack
  }>({ signature, stack: [undefined] })

  // On the render where filters change, treat the stack as reset immediately so
  // we never fetch with a stale cursor; the effect below syncs the state.
  const stack: CursorStack =
    pageState.signature === signature ? pageState.stack : [undefined]

  useEffect(() => {
    if (pageState.signature !== signature) {
      setPageState({ signature, stack: [undefined] })
    }
  }, [signature, pageState.signature])

  const currentCursor = stack[stack.length - 1]

  const {
    data,
    isPending,
    isError,
    error,
    dataUpdatedAt,
    isFetching,
    refetch,
  } = useJetAssignmentsQuery({
    limit: _PAGE_SIZE,
    cursor: currentCursor,
    statuses: filters.statuses,
    assignmentId: filters.assignmentId || null,
    // Digit-only input (see filter bar), so Number() is always a valid id or ''.
    shiftId: filters.shiftId ? Number(filters.shiftId) : null,
    dateField: filters.dateField,
    daysAgo: filters.daysAgo,
    sortBy: filters.sortBy,
    sortDir: filters.sortDir,
  })

  const items = data?.items ?? []
  const nextCursor = data?.nextCursor ?? null

  const value: JetAssignmentsContextState = {
    state: {
      filters,
      items,
      totalCount: data?.totalCount ?? 0,
      pageIndex: stack.length,
      hasNextPage: Boolean(nextCursor),
      hasPrevPage: stack.length > 1,
      isPending,
      isFetching,
      isError,
      error: parseErrorMessage(error),
      dataUpdatedAt,
    },
    actions: {
      setFilters,
      goNextPage: () => {
        if (!nextCursor) return
        setPageState({ signature, stack: [...stack, nextCursor] })
      },
      goPrevPage: () => {
        if (stack.length <= 1) return
        setPageState({ signature, stack: stack.slice(0, -1) })
      },
      refetch: () => {
        void refetch()
      },
    },
  }

  return <Context.Provider value={value}>{children}</Context.Provider>
}

export { useJetAssignmentsContextValue }
